/**
 * ChunkLoadRecovery
 *
 * Recupera a SPA de um `ChunkLoadError` recarregando a página uma vez.
 *
 * Por que existe: o app é servido do S3 atrás do CloudFront, e o deploy publica
 * apagando o build anterior. Uma aba aberta antes do deploy segue com o runtime
 * do build velho em memória e, ao navegar para uma rota lazy, pede um chunk que
 * não existe mais. O CloudFront tem SPA fallback (403/404 → `/app/index.html`
 * com HTTP **200**), então o `<script>` "carrega com sucesso" trazendo HTML onde
 * o webpack esperava JS — o chunk nunca se registra e o runtime lança
 * `ChunkLoadError`. Sem tratamento, a tela fica quebrada até o usuário
 * recarregar na mão. Concentra-se no `/payment`, o último passo do funil.
 *
 * O reload cura porque o navegador revalida o documento principal (reload
 * programático manda `max-age=0`), buscando o `index.html` do build novo — que
 * referencia chunks que existem. A retenção de builds no S3
 * (`.gitlab/scripts/deploy-s3-retencao.sh`) ataca a causa; isto aqui é a rede de
 * segurança para a janela em que o cliente já está com o runtime velho.
 *
 * Regras de sobrevivência (um recuperador que erra é pior que a doença):
 *  - NUNCA lança — é chamado de dentro do ErrorHandler global;
 *  - NUNCA recarrega sem poder registrar que recarregou. Sem `sessionStorage`
 *    não há como frear, e um erro no boot viraria loop infinito de reload;
 *  - teto duplo: um reload por COOLDOWN_MS (freia o loop rápido, quando o
 *    próprio boot falha) e MAX_RELOADS por sessão (freia o loop lento, quando o
 *    chunk some de vez — cache envenenado, rede corporativa filtrando).
 */

/** Só o que este recuperador usa de `sessionStorage` — facilita o teste. */
export interface StorageMinimo {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Decisão tomada para uma ocorrência. Viaja no `ClientAppError` (campo
 * `recovery`), então dá para medir no Athena quantos ChunkLoadError viraram
 * reload e quantos bateram no freio — sem isso a cura é invisível.
 */
export type DecisaoRecuperacao =
  | ''            // não é ChunkLoadError: nada a fazer
  | 'reload'      // recarregando agora
  | 'cooldown'    // recarregou há pouco (provável loop): freado
  | 'max'         // já recarregou MAX_RELOADS nesta sessão: freado
  | 'no_storage'; // sem sessionStorage: não dá para frear, então não recarrega

interface EstadoReload {
  /** Reloads já disparados nesta sessão de navegação. */
  n: number;
  /** Epoch (ms) do último reload disparado. */
  ts: number;
}

const ESTADO_ZERO: EstadoReload = { n: 0, ts: 0 };

/**
 * Assinaturas de "chunk lazy não carregou", nos dois motores:
 *  - webpack (build atual): `ChunkLoadError: Loading chunk 12 failed.` e a
 *    variante de CSS;
 *  - import dinâmico nativo (esbuild/Vite — o que o épico de upgrade traz):
 *    Chrome `Failed to fetch dynamically imported module`, Firefox `error
 *    loading dynamically imported module`, Safari `Importing a module script
 *    failed`. Já ficam cobertos para não precisar redescobrir isto depois.
 */
const PADROES_CHUNK = [
  /loading (css )?chunk \S+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
];

/**
 * `sessionStorage` acessível? O simples acesso à propriedade lança
 * `SecurityError` quando cookies estão bloqueados (iOS/Safari, webview
 * embutida), e o `setItem` lança em modo privado antigo — por isso a sonda é
 * uma escrita de verdade, não um `typeof`.
 */
function sessionStorageSeguro(): StorageMinimo | null {
  try {
    const s = window.sessionStorage;
    if (!s) return null;
    const sonda = 'mp:probe';
    s.setItem(sonda, '1');
    s.removeItem(sonda);
    return s;
  } catch (e) {
    return null;
  }
}

/**
 * A URL de ENTRADA, capturada quando o bundle é avaliado.
 *
 * ── Por que ela precisa existir (10/09/2026) ────────────────────────────────
 *
 * `location.reload()` recarrega a URL CORRENTE. Quando a navegação inicial
 * falha por chunk — que é o caso dominante —, o Angular já fez `restoreHistory`
 * e a barra de endereços voltou para a raiz `/app/` ANTES de o erro chegar aqui.
 * O reload então recarrega `/app/`: o app sobe inteiro, funcionando, **sem
 * loja**. O cliente clicou no link do restaurante e foi parar numa tela que não
 * é de restaurante nenhum.
 *
 * Medido no Athena (08-10/09), devices distintos com `recovery='reload'`:
 * **145 em rota sem loja contra 76 em rota com loja — 65,6%**. Na frota
 * (o instrumento vê ~26,7%) são da ordem de 180 devices/dia.
 *
 * Capturar no momento em que o módulo é avaliado é o que garante pegar a URL
 * antes de qualquer coisa do router: este arquivo é importado pelo
 * `OrderFlowErrorHandler`, que é o primeiro provider que o Angular resolve.
 */
const ENTRADA = (() => {
  try {
    return (typeof window !== 'undefined' && window.location) ? window.location.href : '';
  } catch (e) {
    return '';
  }
})();

/**
 * A rota tem segmento de loja depois do `/app/`?
 *
 * É o que distingue "o router me devolveu para a raiz" de "o cliente está mesmo
 * numa tela sem loja". Só o primeiro caso merece correção.
 */
export function temLoja(href: string): boolean {
  try {
    if (!href) return false;
    const caminho = new URL(href, 'https://mp.invalid/').pathname;
    // Tira a barra inicial e o prefixo `/app` — que está em vias de sair da URL
    // (ver a iniciativa de remover o prefixo) e por isso não pode ser exigido.
    // O que sobrar é o slug da loja; vazio significa raiz do app.
    const resto = caminho.replace(/^\/+/, '').replace(/^app(?:\/|$)/, '');
    return resto.replace(/\/+$/, '').length > 0;
  } catch (e) {
    return false;
  }
}

export class ChunkLoadRecovery {
  static readonly STORAGE_KEY = 'mp:chunk-reload';
  /** Um reload por 10 min: janela larga o bastante para não brigar com o boot. */
  static readonly COOLDOWN_MS = 600000;
  /** Teto por sessão: 3 deploys durante a mesma aba já é caso extremo. */
  static readonly MAX_RELOADS = 3;

  constructor(
    private storage: StorageMinimo | null = sessionStorageSeguro(),
    private recarregarPagina: () => void = () => window.location.reload(),
    private agora: () => number = () => Date.now(),
    /** URL de entrada. Injetável só para o teste; produção usa a capturada. */
    private entrada: string = ENTRADA,
    private urlAtual: () => string = () => window.location.href,
    private trocarUrl: (url: string) => void =
      (url: string) => { window.history.replaceState(null, '', url); },
  ) { }

  /** É falha de carregamento de chunk lazy (e não um erro qualquer)? */
  static ehChunkLoadError(name: string, message: string): boolean {
    if (name === 'ChunkLoadError') return true;
    const msg = String(message || '');
    for (const padrao of PADROES_CHUNK) {
      if (padrao.test(msg)) return true;
    }
    return false;
  }

  /**
   * Decide o que fazer com esta ocorrência — SEM efeito colateral.
   *
   * Separado de `recarregar()` de propósito: o handler precisa do veredito para
   * pôr no payload da telemetria ANTES de disparar o reload, senão o evento que
   * explica a recuperação morre junto com a página.
   */
  decidir(name: string, message: string): DecisaoRecuperacao {
    if (!ChunkLoadRecovery.ehChunkLoadError(name, message)) return '';
    if (!this.storage) return 'no_storage';

    const estado = this.lerEstado();
    if (estado.n >= ChunkLoadRecovery.MAX_RELOADS) return 'max';
    if (estado.ts && this.agora() - estado.ts < ChunkLoadRecovery.COOLDOWN_MS) return 'cooldown';
    return 'reload';
  }

  /**
   * Registra o reload e recarrega. Gravar ANTES é o que impede o loop: se a
   * página nova falhar de novo, o freio já está armado.
   */
  recarregar(): void {
    const estado = this.lerEstado();
    this.gravarEstado({ n: estado.n + 1, ts: this.agora() });
    this.restaurarEntrada();
    this.recarregarPagina();
  }

  /**
   * Devolve a barra de endereços à URL de entrada ANTES do reload, quando o
   * router já a tinha zerado.
   *
   * **`replaceState` + `reload()`, e não `location.replace(url)`** — a diferença
   * é o que faz a correção curar em vez de só mudar de tela. Navegar para uma
   * URL usa o cache HTTP normalmente e pode servir de volta o MESMO documento
   * velho que causou o erro; só o reload manda revalidar, e ele revalida a URL
   * corrente. Trocando a URL primeiro, o reload revalida a chave CANÔNICA da
   * loja — que é justamente a entrada que precisava ser refrescada.
   *
   * Só age no caso medido: corrente sem loja, entrada com loja. Quem já está
   * numa rota de loja recarrega exatamente como antes — o caminho de menor
   * surpresa para os 34,4% que hoje já acertam o alvo.
   *
   * Nunca lança: falhar aqui só devolve o comportamento de hoje.
   */
  private restaurarEntrada(): void {
    try {
      const atual = this.urlAtual();
      if (!this.entrada || this.entrada === atual) return;
      if (temLoja(atual) || !temLoja(this.entrada)) return;
      this.trocarUrl(this.entrada);
    } catch (e) {
      // Sem `replaceState` (motor antigo, sandbox) o reload segue como sempre.
    }
  }

  private lerEstado(): EstadoReload {
    try {
      const cru = this.storage ? this.storage.getItem(ChunkLoadRecovery.STORAGE_KEY) : null;
      if (!cru) return ESTADO_ZERO;
      const parsed = JSON.parse(cru);
      // Estado corrompido (usuário/extensão mexeu, formato antigo) vale zero —
      // recuperar de um erro não pode depender de dado bem formado.
      const n = typeof parsed.n === 'number' && isFinite(parsed.n) ? parsed.n : 0;
      const ts = typeof parsed.ts === 'number' && isFinite(parsed.ts) ? parsed.ts : 0;
      return { n, ts };
    } catch (e) {
      return ESTADO_ZERO;
    }
  }

  private gravarEstado(estado: EstadoReload): void {
    try {
      if (this.storage) {
        this.storage.setItem(ChunkLoadRecovery.STORAGE_KEY, JSON.stringify(estado));
      }
    } catch (e) {
      // Storage cheio/bloqueado no meio do caminho: o reload segue, e o pior
      // caso volta a ser o de hoje (tela quebrada), nunca um loop — o `decidir`
      // já exigiu storage legível para chegar até aqui.
    }
  }
}
